import {
  FC,
  PropsWithChildren,
  createContext,
  useCallback,
  useState
} from 'react';
import * as Sentry from '@sentry/browser';
import { updateKeycloakToken } from 'src/api/capacitor-adapter';
import { SplashScreen } from 'src/components/splash-screen';
import {
  useNotificationsContext,
  useTenantContext
} from 'src/hooks/use-context';
import { useHookMemo } from 'src/hooks/use-hook-memo';
import { useMountEffect } from 'src/hooks/use-mount-effect';
import {
  KeycloakToken,
  initializeKeycloak,
  keycloakSignInWithEmailAndPassword,
  keycloakSignOut
} from 'src/libs/keycloak';
import { messageServiceWorker } from 'src/libs/sw-registration';
import { AuthContextType, addSession, getSession } from './auth-context';

export interface KeycloakCallbacks {
  // signInWithEmailLink: (email: string, url: string) => Promise<void>;
  signInWithEmailAndPassword: (
    email: string,
    password: string
  ) => Promise<void>;
  signOut: (reason?: string) => Promise<void>;
  // updatePassword: (current: string, password: string) => Promise<void>;
  // resetPassword: (email: string) => Promise<void>;
  // resetPasswordWithEmailLink: (
  //   oobCode: string,
  //   password: string
  // ) => Promise<void>;
  // validateOobCode: (oobCode: string) => Promise<string>;
  // development tools
  saveSession: (storageId: string, sessionName: string) => void;
  loadSession: (storage: string) => void;
}

/**
 * Provides user authentication state
 */
export interface KeycloakAuthState {
  // login state
  initialized: boolean;
  authenticated: boolean;
  failed: boolean;
  // keycloak token (provided for re-renders)
  token?: KeycloakToken;
}

const initialState: KeycloakAuthState = {
  initialized: false,
  authenticated: false,
  failed: false
};

export type KeycloakContextType = { type: 'keycloak' } & AuthContextType &
  KeycloakCallbacks &
  KeycloakAuthState;

export const KeycloakContext = createContext<KeycloakContextType | undefined>(
  undefined
);

const handleKeycloakAuthChanged = (
  token: KeycloakToken | null,
  error: unknown
) => {
  console.info('[keycloak] Authentication changed:', !!token);

  // TODO: errors are currently not handled properly by the keycloak lib
  // (therefore failed must not be set to true, which would block the UI)
  if (error !== undefined)
    return {
      initialized: true,
      authenticated: false,
      failed: false
    };

  return {
    initialized: true,
    authenticated: !!token,
    failed: false,
    token: token || undefined
  };
};

/**
 * Helper to improve authentication debugging
 */
const withDebugContext = async <T extends any>(
  debugMessage: string,
  debugExtra: Record<string, unknown>,
  callback: () => Promise<T>
) => {
  console.info(`[keycloak] ${debugMessage}`);
  return callback().catch((error) => {
    // ensure the error is properly captured with Sentry
    // (this is critical for login error debugging)
    Sentry.captureException(error, {
      extra: {
        ...debugExtra,
        currentTenantId: 'TODO!'
      }
    });
    // re-throw the error to the caller
    throw error;
  });
};

/**
 * Provide the keycloak logic to children
 *
 * NOTE: This component blocks rendering until the keycloak context has been
 * initialized properly. Children can rely on the context being ready.
 */
export const KeycloakProvider: FC<PropsWithChildren> = (props) => {
  const { children } = props;
  const [state, setState] = useState<KeycloakAuthState>(initialState);
  const { handleRequestPermissions } = useNotificationsContext();
  const { tenant } = useTenantContext();

  const handleState = useCallback(
    async (state: KeycloakAuthState) => {
      setState(state);
      // provide the token to the service worker
      await messageServiceWorker('KEYCLOAK_TOKEN', state.token);
    },
    // must remain stable to avoid re-rendering the context
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // initialize keycloak
  useMountEffect(() => {
    console.info('[keycloak] Initialization');
    if (!globalThis.__KEYCLOAK__)
      throw new Error('[keycloak] Missing configuration');

    initializeKeycloak(
      globalThis.__KEYCLOAK__.clientId,
      globalThis.__KEYCLOAK__.baseUrl,
      tenant?.tenantId!!,
      async (token, error) => {
        console.info(`[keycloak] Token changed`, !!token);
        // inject the user into the API (no re-render)
        updateKeycloakToken(token);
        // update the local state (and cause re-render after API is ready)
        handleState(handleKeycloakAuthChanged(token, error));
        // request notifications when authentication changes
        if (!!token) await handleRequestPermissions('Keycloak auth changed');
      }
    );
  });

  const signInWithEmailAndPassword = useCallback(
    (email: string, password: string): Promise<void> =>
      withDebugContext('Sign in with email/password', { email }, () =>
        keycloakSignInWithEmailAndPassword(email, password)
      ),
    []
  );

  const signOut = useCallback(
    (): Promise<void> =>
      withDebugContext('Sign out', {}, () => keycloakSignOut()),
    []
  );

  const saveSession = useCallback(
    (storageId: string, sessionName: string): void => {
      // retrieve the underlying firebase token
      const key = 'deinverband:keycloakToken';
      const session = localStorage.getItem(key);
      addSession(storageId, sessionName, session);
    },
    []
  );

  const loadSession = useCallback((storageId: string): void => {
    // restore the underlying firebase token and reload
    const session = getSession(storageId);
    const key = 'deinverband:keycloakToken';
    localStorage.setItem(key, session.session);

    // activate the login
    window.location.reload();
  }, []);

  const context = useHookMemo({
    type: 'keycloak' as const,
    // auth state
    initialized: state.initialized,
    authenticated: state.authenticated,
    failed: state.failed,
    // keycloak callbacks
    signInWithEmailAndPassword,
    signOut,
    // not supported in this context
    updatePassword: undefined,
    // development tools
    saveSession,
    loadSession
  });

  // children rely on valid context
  if (state.failed) return <SplashScreen variant="error" />;
  if (!state.initialized) return <SplashScreen />;

  return (
    <KeycloakContext.Provider value={context}>
      {children}
    </KeycloakContext.Provider>
  );
};
